import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams, useParams } from "react-router-dom";
import { useVideoContext } from "../context/video";
import axiosInstant from "../../../../api";
import { getVideoEndpoint, getBrandProjectVideosEndpoint, deleteVideoUGC } from "../../../../api/endpoints";
import { useUser } from "../../../../hooks/useUser";
import { isInternal } from "../../../../utils";
import type { ProjectWithDeliveredDate } from "../../../videoProduction/helper";
import { useWorkspaceBrandId } from "../../../../hooks/useWorkspaceBrandId";

enum VideoType {
    UGC,
    TDC,
    XGC
}

interface IFetchVideoParams {
    limitParam: number;
    videoIdParam?: string | null;
    searchStringParam?: string | null;
    productIdParam?: string | null;
    productCategoryIdParam?: string | null;
    expertIdParam?: string | null;
    aspectRatioParam?: string | null;
    ccParam?: string | null;
    extraParam?: string | null;
    embedIdParam?: string | null;
}

export interface IVideo {
    bucket: string;
    s3_file_id?: number;
    cloudFrontDomain?: string;
    description?: string;
    id: number;
    hash?: string;
    // Internal-only production sub-status of this deliverable; present only for
    // internal usertypes (scrubbed server-side otherwise).
    internal_status?: string | null;
    isPublic: boolean;
    // Proposal example flag — surfaces the video on public proposal pages/PDF.
    demo?: boolean;
    key: string;
    title?: string;
    name?: string;
    projectId?: number;
    s3Location: string;
    location: string;
    type: string;
    reviewS3File?: {
        s3Location: string;
        location: string;
    };
    rating: number;
    review_text?: string;
    author?: {
        first_name: string;
        last_name: string;
    };
    aspect_ratio?: string;
    /** Frames per second probed on import (null before the probe sent it). */
    fps?: number | null;
    /** Bytes of the source file probed on import; BIGINT, so a string from pg. */
    file_size?: number | string | null;
    length?: any;
    version?: number;
    videoPros?: Array<{
        slug: string;
        userid: number;
        firstName: string;
        lastName: string;
        displayName: string;
        description: string;
        short_description: string;
        intro_video: any;
        avatar: string;
        xgc_ready: boolean;
        featured: any;
        bucket: string;
        video1: string;
        video2: string;
        video3: string;
        yearsExperience: string;
        totalReach: number;
        facebook: string;
        twitter: string;
        instagram: string;
        tiktok: string;
        youtube: string;
        pinterest: string;
        linkedin: string;
        website: string;
        categories: any[];
        interests: any[];
    }>;
    videoProducts?: Array<{
        image?: string;
        title?: string;
        desireid?: string;
    }>;
    // PDP URLs this video is on (its links) and how many of those the last
    // PDP analysis actually found it on. List endpoints attach both.
    pdp_url_count?: number;
    placed_count?: number;
    brandOwners?: Array<{
        id: number;
        name: string;
        description: string;
        site_url: string;
        sort_order: number;
        cover_image: string;
        logo: string;
        deactivated: boolean;
        published: boolean;
        embed: boolean;
        slug: string;
        cc: boolean;
        pre_approval_downloading?: boolean;
        enabled_file_types?: string[];
    }>;
    brand?: {
        id: number;
        name: string;
        pre_approval_downloading?: boolean;
        enabled_file_types?: string[];
    };
    published: boolean;
    published_public: boolean;
    createdAt: string;
    updatedAt: string;
    source: string;
    review: any;
    final?: boolean;
    master?: boolean;
    thumbnail_count?: number;
    // The deliverable's channel: the generic channel plus the project channel
    // INSTANCE it was cut for (a project may hold 2× PDP). The single-file
    // endpoint attaches both with the project's instances so the details page
    // can label duplicates ("PDP #2") like the Downloads tab.
    content_channel_id?: number | null;
    brand_project_channel_id?: number | null;
    content_channel?: { id?: number; name?: string } | null;
    project?: ProjectWithDeliveredDate & {
        content_type?: { id?: number; name?: string } | null;
        project_channels?: Array<{
            id: number;
            content_channel_id?: number;
            label?: string | null;
            content_channel?: { id?: number; name?: string } | null;
        }>;
    };
    option_selection_links?: Array<{
        id?: number;
        selection_id?: number;
        s3_file_id?: number;
        selection?: {
            id?: number;
            numeric_value?: number | null;
            option_value?: {
                id?: number;
                name?: string;
                default?: boolean;
                option?: {
                    id?: number;
                    name?: string;
                    type?: string;
                    unit?: string | null;
                };
            };
        };
    }>;
}

export const formatVideoName = (input: string | null | undefined) => {
    if (!input) return "";
    const nameinput = input.replace(/^.*[\\/]/, "");
    const arr = nameinput.split("_");
    const regexp = /[-, _]/g;
    const output = arr[0].replace(regexp, " ");
    return output
        .split(" ")
        .reduce((prev: string[], current) => {
            if (current[0]) {
                prev.push(current[0].toUpperCase() + current.slice(1));
            }
            return prev;
        }, [])
        .join(" ");
};

export const useFetchVideos = (
    videoType: keyof typeof VideoType,
    prodId?: any,
    disableSearchParams?: boolean,
    legacyMode?: boolean
) => {
    const { user, role, brand } = useUser();
    // Inside a client workspace internal staff fetch that brand's videos,
    // not the all-brands set (TDC-1609).
    const workspaceBrandId = useWorkspaceBrandId();
    let brandId = workspaceBrandId || (Array.isArray(brand) ? brand[0].id : 0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<null | string>(null);
    const [videos, setVideos] = useState<IVideo[]>([]);
    const [count, setCount] = useState(0);
    const [limit, setLimit] = useState<number>(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const { brandId: managerBrandId } = useParams();

    const {
        setVideoId,
        setProductId,
        setProductCategoryId,
        setExtraFilter,
        setContentCode,
        setAspectRatio,
        setSearchString,
        setEmbed,
        setProId: setExpertId
    } = useVideoContext();

    // make sure user existed
    if (!user) throw new Error("User not existed");

    const getVideos = async (payload: any) => {
        if (managerBrandId && isInternal(role)) brandId = managerBrandId;
        const endpoint =
            videoType === "TDC" && !legacyMode
                ? getBrandProjectVideosEndpoint(brandId, payload)
                : getVideoEndpoint(brandId, videoType, payload);
        const response = await axiosInstant.get(endpoint);
        return response;
    };

    const deleteUGC = async (reviewId: any) => {
        const endpoint = deleteVideoUGC(brandId, reviewId);
        const response = await axiosInstant.delete(endpoint);
        return response;
    };

    const updateVideoId = (id: number | null) => setVideoId(id || 0);

    // sets the variables from the name value pairs in the url
    const getSearchParams = (): IFetchVideoParams => {
        const limitString = searchParams.get("limit");
        const videoIdParam = searchParams.get("video");
        const searchStringParam = searchParams.get("search");
        const productIdParam = searchParams.get("productId");
        const productCategoryIdParam = searchParams.get("productCategoryId");
        const expertIdParam = searchParams.get("proId");
        const embedIdParam = searchParams.get("embed");
        const aspectRatioParam = searchParams.get("aspectRatio");
        const ccParam = searchParams.get("contentCode");
        const extraParam = searchParams.get("extraFilter");

        if (disableSearchParams)
            return {
                limitParam: 0,
                videoIdParam,
                searchStringParam: "",
                productIdParam: "",
                productCategoryIdParam: "",
                expertIdParam: "",
                aspectRatioParam: "",
                ccParam: "",
                extraParam: "",
                embedIdParam: ""
            };
        return {
            limitParam: parseInt(limitString || "20", 10),
            videoIdParam,
            searchStringParam,
            productIdParam,
            productCategoryIdParam,
            expertIdParam,
            aspectRatioParam,
            ccParam,
            extraParam,
            embedIdParam
        };
    };

    // sets the url name value pairs
    const updateSearchParams = (params: IFetchVideoParams) => {
        const {
            limitParam,
            videoIdParam,
            searchStringParam,
            productIdParam,
            productCategoryIdParam,
            expertIdParam,
            aspectRatioParam,
            ccParam,
            extraParam,
            embedIdParam
        } = params;
        setLimit(limitParam);
        searchParams.set("limit", String(limitParam));

        setVideoId(parseInt(videoIdParam || "0", 10));
        setSearchString(searchStringParam || "");
        setProductId(productIdParam);
        setProductCategoryId(productCategoryIdParam || "");
        setExpertId(expertIdParam);
        setAspectRatio(aspectRatioParam || "");
        setContentCode(ccParam || "");
        setExtraFilter(extraParam || "");
        setEmbed(embedIdParam || "");
    };

    useEffect(() => {
        if (limit) fetchVideos();
    }, [searchParams]);

    useEffect(() => {
        if (!limit) {
            const limitValue = searchParams.get("limit") || "20";
            const limitNumber = parseInt(limitValue, 10);
            setLimit(limitNumber);
            setSearchParams((params) => {
                params.set("limit", limitValue);
                return params;
            });
            setTimeout(() => fetchVideos({ limit: limitNumber, offset: 0 }), 300);
        }
    }, [limit]);

    const fetchVideos = async (
        customPagination?: {
            offset?: number;
            limit?: number;
            shouldAppend?: boolean;
        },
        customQuery?: { [key: string]: string }
    ) => {
        const params = getSearchParams();
        const {
            limitParam,
            searchStringParam,
            productIdParam,
            productCategoryIdParam,
            aspectRatioParam,
            ccParam,
            extraParam,
            embedIdParam,
            expertIdParam
        } = params;

        if (user) {
            try {
                setLoading(true);
                const response = await getVideos({
                    pagination: {
                        limit: disableSearchParams ? 1000 : 20,
                        offset: disableSearchParams ? 0 : Math.max(limitParam - 20, 0),
                        ...(customPagination || {})
                    },

                    productId: prodId || productIdParam,
                    prodId,
                    productCategoryId: productCategoryIdParam,

                    search: searchStringParam,
                    aspectRatio: aspectRatioParam,
                    contentCode: ccParam,
                    extraFilter: extraParam,
                    embedId: embedIdParam,
                    proId: expertIdParam,
                    ...(customQuery || {})
                });
                const { status, data } = response;
                if (status === 200 && data.rows) {
                    const shouldAppend = limitParam > limit;
                    let videosArray = [...data.rows];
                    if (shouldAppend || customPagination?.shouldAppend)
                        videosArray = [...(videos || []), ...(data.rows || [])];
                    setVideos(videosArray);
                    setCount(data.count);
                    if (!disableSearchParams) updateSearchParams(params);
                }
            } catch (e) {
                setError((e as Error).message);
                toast.error((e as Error).message);
            } finally {
                setLoading(false);
            }
        }
    };

    const loadMore = () => {
        fetchVideos({ offset: videos.length, limit: 20, shouldAppend: true });
    };

    return {
        fetchVideos,
        deleteUGC,
        loadMore,
        videos,
        setVideos,
        loading,
        error,
        total: count,
        updateVideoId
    };
};
